/**
 * <s-motion> — CharlyCorp storefront motion layer (no external libraries).
 * Loader, masked line titles, scroll reveal, parallax, card tilt + glare,
 * magnetic buttons, hide-on-scroll header, reading progress, cart pop + toast,
 * and the mobile sticky buy bar. Decorative motion is skipped for reduced motion;
 * tilt and magnetic only run for fine pointers. Content is never hidden unless this runs.
 */
class SMotion extends HTMLElement {
  #ac;
  #raf = 0;
  #parallax = [];
  #lastY = 0;

  connectedCallback() {
    const root = document.documentElement;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#loader(root, reduced);
    this.#stickyBar();
    this.#cartFeedback(signal);
    this.#announce(signal);
    this.#header(signal);
    if (reduced) return;

    document.querySelectorAll('[data-lines]').forEach((el) => el.classList.add('cc-lines-ready'));
    this.#reveal();
    this.#setupParallax(signal);
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#tilt(signal);
      this.#magnetic(signal);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
    clearInterval(this.#announceTimer);
  }

  #announceTimer = 0;

  /** Announcement bar: shows one message at a time, every 4s; pauses on hover/focus; the close button hides it for the session. */
  #announce(signal) {
    const bar = document.querySelector('[data-announce]');
    if (!bar) return;
    bar.querySelector('[data-announce-close]')?.addEventListener(
      'click',
      () => {
        bar.hidden = true;
        clearInterval(this.#announceTimer);
        try {
          sessionStorage.setItem('cc-announce-closed', '1');
        } catch {}
      },
      { signal }
    );
    const items = [...bar.querySelectorAll('[data-announce-item]')];
    if (items.length < 2 || bar.hidden) return;
    let index = 0;
    let paused = false;
    const pause = () => (paused = true);
    const resume = () => (paused = false);
    bar.addEventListener('pointerenter', pause, { signal });
    bar.addEventListener('pointerleave', resume, { signal });
    bar.addEventListener('focusin', pause, { signal });
    bar.addEventListener('focusout', resume, { signal });
    this.#announceTimer = setInterval(() => {
      if (paused || document.hidden) return;
      items[index].toggleAttribute('data-hidden', true);
      index = (index + 1) % items.length;
      items[index].toggleAttribute('data-hidden', false);
    }, 4000);
  }

  // Counts to 100 while the page loads, then lifts the curtain. The head script guarantees removal after 2.4s.
  #loader(root, reduced) {
    const loader = document.querySelector('.cc-loader');
    if (!loader || !root.classList.contains('cc-loading')) return;
    const percent = loader.querySelector('[data-load-percent]');
    let value = 0;
    let loaded = document.readyState === 'complete';
    window.addEventListener('load', () => (loaded = true), { once: true });
    const tick = () => {
      const target = loaded ? 100 : 85;
      value = Math.min(target, value + (loaded ? 10 : 4)); // spec: loader visible 1.5 s max
      loader.style.setProperty('--cc-load', String(value / 100));
      if (percent) percent.textContent = `${Math.round(value)}%`;
      if (value < 100) {
        setTimeout(tick, 30);
        return;
      }
      loader.setAttribute('data-done', '');
      setTimeout(() => root.classList.remove('cc-loading'), reduced ? 0 : 400);
    };
    tick();
  }

  // Only elements that start below the fold are hidden, so nothing visible flashes on load.
  #reveal() {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.removeAttribute('data-reveal-pending');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.setAttribute('data-reveal-pending', '');
        io.observe(el);
      }
    });
  }

  #setupParallax(signal) {
    // Container background images are rendered by the container block; tag them here so they join the parallax set.
    document.querySelectorAll('[data-parallax-bg]').forEach((scope) => {
      const img = scope.querySelector(':scope > img');
      if (img) img.dataset.parallax = scope.dataset.parallaxBg || '0.1';
    });
    this.#parallax = [...document.querySelectorAll('[data-parallax]')];
    if (!this.#parallax.length) return;
    const update = () => {
      this.#raf = 0;
      const vh = window.innerHeight;
      this.#parallax.forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const factor = Number(el.dataset.parallax) || 0;
        const offset = (rect.top + rect.height / 2 - vh / 2) * -factor;
        el.style.translate = `0 ${offset.toFixed(1)}px`;
      });
    };
    const schedule = () => {
      if (!this.#raf) this.#raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    update();
  }

  // Header hide/show + blur, and the reading progress bar, share one scroll listener.
  #header(signal) {
    const header = document.querySelector('[data-cc-header]');
    const progress = document.querySelector('.cc-progress');
    const onScroll = () => {
      const y = window.scrollY;
      if (header) {
        header.toggleAttribute('data-scrolled', y > 40);
        const goingDown = y > this.#lastY && y > 120;
        const menuOpen = header.querySelector('.nav-has-dropdown:hover, :focus-within');
        header.toggleAttribute('data-hidden', goingDown && !menuOpen);
      }
      if (progress) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progress.style.setProperty('--cc-progress', max > 0 ? String(Math.min(1, y / max)) : '0');
      }
      this.#lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();
  }

  #tilt(signal) {
    document.querySelectorAll('[data-tilt]').forEach((card) => {
      card.addEventListener(
        'pointermove',
        (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          card.style.transform = `perspective(900px) rotateX(${((0.5 - y) * 24).toFixed(2)}deg) rotateY(${((x - 0.5) * 24).toFixed(2)}deg)`;
          card.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
          card.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
        },
        { signal }
      );
      card.addEventListener('pointerleave', () => (card.style.transform = ''), { signal });
    });
  }

  #magnetic(signal) {
    document.querySelectorAll('[data-magnetic]').forEach((button) => {
      button.addEventListener(
        'pointermove',
        (event) => {
          const rect = button.getBoundingClientRect();
          const x = event.clientX - rect.left - rect.width / 2;
          const y = event.clientY - rect.top - rect.height / 2;
          button.style.translate = `${(x * 0.25).toFixed(1)}px ${(y * 0.35).toFixed(1)}px`;
        },
        { signal }
      );
      button.addEventListener('pointerleave', () => (button.style.translate = ''), { signal });
    });
  }

  // The cart-count partial is replaced after each mutation; when the count grows, pop the badge and show a toast.
  #cartFeedback(signal) {
    const read = () => Number(document.querySelector('[data-cart-count]')?.dataset.cartCount || 0);
    let count = read();
    const message = this.dataset.addedMessage;
    const observer = new MutationObserver(() => {
      const next = read();
      if (next > count) {
        const badge = document.querySelector('[data-cart-count] [data-cart-badge]');
        badge?.classList.add('cc-pop');
        if (message) {
          document.dispatchEvent(new CustomEvent('s-notification:add', { detail: { message, variant: 'success', duration: 3000 } }));
        }
      }
      count = next;
    });
    observer.observe(document.body, { childList: true, subtree: true });
    signal.addEventListener('abort', () => observer.disconnect());
  }

  // Shows the mobile buy bar only once the real add-to-cart button has left the screen.
  #stickyBar() {
    const bar = document.querySelector('[data-sticky-bar]');
    const sentinel = document.querySelector('[data-sticky-sentinel]');
    if (!bar || !sentinel) return;
    const io = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.toggleAttribute('data-visible', passed);
      bar.toggleAttribute('inert', !passed);
    });
    io.observe(sentinel);
  }
}

if (!customElements.get('s-motion')) customElements.define('s-motion', SMotion);
